import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DefaultNodeOptions,
  JsonError,
  NumberNode,
  widget,
  WidgetPlugin,
} from '@sagold/react-json-editor';

import { FormField } from '../../../components/formfield/FormField';
import { NumberInput } from '../../../components/numberinput/NumberInput';
import { ReadOnlyInput } from '../../../components/readonly-input/ReadOnlyInput';
import {
  FormatterOptions,
  numberFormatter,
  postfixFormatter,
} from '../../../components/table/valueFormatters';
import {
  getDecimalSeparatorForLocale,
  getThousandsSeparatorForLocale,
} from '../../../utils/numberLocale';
import {
  isNullableInteger,
  isNullableNumber,
  resolvePlaceholder,
  useErrorTransformation,
} from '../widgetUtils';

import { resolveNumberInputBounds } from './numberInputBounds';
import { formatNumberPlaceholderExample } from './numberPlaceholder';

export type NumberOptions = {
  digits?: number;
  disableThousandsSeparator?: boolean;
  icon?: string;
  tag?: string;
  postfix?: string;
  swapIconPosition?: boolean;
  format?: Intl.NumberFormatOptions;
  /* add increment/decrement buttons */
  withButtons?: boolean;
  tableLayout?: boolean;
  showTitle?: boolean;
  showErrorWithColor?: boolean;
} & DefaultNodeOptions;

function errorsToString(errors: JsonError[]) {
  if (errors.length === 0) {
    return;
  }
  return errors.map((e) => e.message).join('\n');
}

function toNumber<N>(value: unknown, nan: N): number | N {
  const decimalSeparator = getDecimalSeparatorForLocale() ?? '.';
  let normalized = String(value);
  if (decimalSeparator !== '.') {
    normalized = normalized.replace(decimalSeparator, '.');
  }

  const number = Number.parseFloat(normalized);
  return Number.isNaN(number) ? nan : number;
}

// Don't commit half-baked number drafts e.g "5."
function isIncompleteNumberDraft(
  value: string,
  disableThousandsSeparator: boolean
): boolean {
  const decimalSeparator = getDecimalSeparatorForLocale() ?? '.';
  if (value.endsWith(decimalSeparator)) {
    return true;
  }

  const thousandsSeparator = getThousandsSeparatorForLocale(
    disableThousandsSeparator
  );
  return Boolean(thousandsSeparator && value.endsWith(thousandsSeparator));
}

export const NumberWidget = widget<NumberNode<NumberOptions>, number | null>(
  ({ node, options, setValue }) => {
    const { t } = useTranslation();
    const hasError = node.errors.length > 0;
    const isValidConst = node.schema.const != null && !hasError;

    const errors = useErrorTransformation(node.errors);
    const { min, max } = resolveNumberInputBounds(node.schema, options.digits);
    const example = formatNumberPlaceholderExample(node.schema, options);
    const placeholder = resolvePlaceholder(
      options.placeholder,
      t('app.forms.placeholders.number', { example: example ?? '1' })
    );

    const [inputValue, setInputValue] = useState<number | string>(
      node.value ?? ''
    );

    useEffect(() => {
      setInputValue((current) => {
        if (typeof current === 'string' && current !== '') {
          // Keep Mantine intermediate strings ("-", "-0", "-0.", "5.") while
          // the editor value is still empty.
          if (node.value == null) {
            return current;
          }
          // Same number as editor → keep typed string ("5.0", "5.00") so
          // trailing zeros are not collapsed when setValue commits the number.
          if (toNumber(current, Number.NaN) === node.value) {
            return current;
          }
        }
        return node.value ?? '';
      });
    }, [node.value]);

    const handleChange = (value: string | number) => {
      // Typing "-" on a 0 field yields the string "-0". Treat that as a leading
      // minus draft, but keep "-0" once the user has already started with "-".
      const nextValue =
        value === '-0' && ['', 0, '0'].includes(inputValue) ? '-' : value;

      // Mantine keeps incomplete values as strings (e.g. "-", "-0", "-0.") and
      // only emits a number once the value is complete.
      setInputValue(nextValue);

      if (typeof nextValue === 'number') {
        setValue(nextValue);
      } else if (typeof nextValue === 'string') {
        // "5.0" is sent as string by mantine, but we want to parse and store it as number
        const parsedValue = toNumber(nextValue, Number.NaN);
        if (
          !Number.isNaN(parsedValue) &&
          !isIncompleteNumberDraft(
            nextValue,
            options.disableThousandsSeparator ?? false
          )
        ) {
          setValue(parsedValue);
          return;
        }

        if (node.value != null) {
          setValue(null);
        }
      }
    };

    const handleBlur = () => {
      if (typeof inputValue === 'string') {
        const value = toNumber(inputValue, null);
        setInputValue(value ?? '');
        setValue(value);
      }
    };

    const readOnlyNumber = postfixFormatter(
      numberFormatter(node.value, options as FormatterOptions),
      options as FormatterOptions
    );
    return (
      <FormField
        widgetType="number"
        node={node}
        errors={errors}
        options={options}
        tableLayout={options.tableLayout}
      >
        {options.readOnly ? (
          <ReadOnlyInput
            description={options.description}
            label={options.title}
            showTitle={options.showTitle !== false}
            value={readOnlyNumber?.toString()}
            placeholder={options.placeholder}
            tableLayout={options.tableLayout}
            hasError={options.showErrorWithColor && node.errors.length > 0}
          />
        ) : (
          <NumberInput
            allowDecimal={isNullableNumber(node.schema.type)}
            decimalScale={options.digits}
            description={options.description}
            disableThousandsSeparator={options.disableThousandsSeparator}
            // Do not clamp the input as this sometimes leads to the user not being able to enter any value.
            // Validation is handled by JSON Editor anyway.
            clampBehaviour="none"
            disabled={options.disabled || isValidConst}
            error={errorsToString(errors)}
            id={node.id}
            min={min}
            max={max}
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={placeholder}
            readOnly={options.readOnly}
            required={options.required}
            suffix={options.postfix}
            tableLayout={options.tableLayout}
            label={options.showTitle !== false && options.title}
            value={inputValue}
          />
        )}
      </FormField>
    );
  }
);

export const NumberWidgetPlugin: WidgetPlugin = {
  id: 'number-widget',
  use: (node) =>
    isNullableNumber(node.schema.type) || isNullableInteger(node.schema.type),
  Widget: NumberWidget,
};
